// client/src/features/salesCoach/components/AITrainer.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { salesCoachApi } from "../api/salesCoachApi";
import { ArrowLeft, Square, Send, Brain, Sparkles, MessageSquarePlus } from "lucide-react";
import Loader from "@/shared/components/ui/Loaders/Loader";
import MemoryRules from "./MemoryRules";

const AITrainer = ({ id }) => {
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  // View: 'rules' shows saved rules; 'chat' is the trainer conversation
  const [view, setView] = useState("chat");
  const [justExtracted, setJustExtracted] = useState(false);
  const [extractingRules, setExtractingRules] = useState(false);
  const [startingChat, setStartingChat] = useState(false);

  // References
  const messageEndRef = useRef(null);

  useEffect(() => {
    loadTrainingAndSession();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadTrainingAndSession = async () => {
    setLoading(true);
    try {
      const t = await salesCoachApi.getTraining(id);
      setTraining(t);

      const existingRules = await salesCoachApi.fetchRules(id);
      if (existingRules.length > 0) {
        setView("rules");
        return;
      }

      await startNewTrainerChat();
    } catch (err) {
      console.error("Error setting up trainer session:", err);
    } finally {
      setLoading(false);
    }
  };

  const startNewTrainerChat = async () => {
    setStartingChat(true);
    try {
      const s = await salesCoachApi.startTrainerSession(id);
      setSession(s.data);
      setMessages(s.messages || []);
      setView("chat");
      setJustExtracted(false);
    } catch (err) {
      console.error("Error starting trainer session:", err);
      alert("Failed to start trainer session. Please try again.");
    } finally {
      setStartingChat(false);
    }
  };

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async (textToSend = input) => {
    const resolvedText = typeof textToSend === "string" ? textToSend : input;
    const messageText = resolvedText.trim();
    if (!messageText || sending) return;

    setInput("");
    setSending(true);

    // Optimistically add user message
    const tempUserMsg = {
      id: Date.now().toString(),
      role: "user",
      content: messageText,
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      const res = await salesCoachApi.sendTrainerMessage(session.id, messageText);

      // Update with server values
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempUserMsg.id),
        res.userMessage,
        res.modelMessage,
      ]);
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setSending(false);
    }
  };

  const handleEndSession = async () => {
    setExtractingRules(true);
    try {
      await salesCoachApi.endTrainerSession(session.id);
      setSession(null);
      setMessages([]);
      setJustExtracted(true);
      setView("rules");
    } catch (err) {
      console.error("Failed to end session:", err);
      alert("Failed to extract rules. Please check server logs.");
    } finally {
      setExtractingRules(false);
    }
  };

  if (loading || extractingRules || startingChat) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader
          text={
            extractingRules
              ? "Extracting rules..."
              : startingChat
                ? "Starting trainer chat..."
                : "Setting up trainer..."
          }
        />
      </div>
    );
  }

  if (view === "rules") {
    return (
      <div className="p-6 max-w-4xl mx-auto min-h-[calc(100vh-60px)]">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 flex items-center">
              <Brain className="w-6 h-6 mr-2 text-secondary" />
              {justExtracted ? "Rules Extracted Successfully" : "Coaching Rules"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {justExtracted
                ? "New rules were merged with your existing coaching guidelines."
                : `Manage coaching guidelines for ${training?.product_name || "this training"}.`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={startNewTrainerChat}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-secondary bg-secondary-ultra-light border border-[#207C9730] rounded-xl hover:bg-secondary-ultra-light/80 transition-colors"
            >
              <MessageSquarePlus className="w-4 h-4 mr-2" />
              Train Again
            </button>
            <Link
              to="/oa/sales-coach"
              className="px-4 py-2 text-sm font-semibold text-white bg-secondary rounded-xl hover:bg-secondary-light shadow-sm"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>

        <MemoryRules trainingId={id} key={justExtracted ? "extracted" : "saved"} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] bg-gray-50">
      {/* Top Header Panel */}
      <div className="px-6 py-4 bg-white border-b border-gray-200 flex justify-between items-center flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          <Link
            to="/oa/sales-coach"
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-secondary bg-secondary-ultra-light px-2 py-0.5 rounded">
              AI Trainer
            </span>
            <h1 className="text-lg font-bold text-gray-800 mt-0.5">
              Training: {training?.product_name}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleEndSession}
            className="inline-flex items-center justify-center px-4 py-2 bg-secondary hover:bg-secondary-light text-white rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-[#207C9720]"
          >
            <Square className="w-4 h-4 mr-2" />
            End Training & Extract Rules
          </button>
        </div>
      </div>

      {/* Main chat layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left instructions panel */}
        <div className="hidden lg:flex w-80 bg-white border-r border-gray-200 p-6 flex-col gap-6 overflow-y-auto">
          <div className="p-4 bg-secondary-ultra-light/50 border border-[#207C9720] rounded-2xl">
            <h3 className="text-sm font-bold text-gray-900 flex items-center">
              <Sparkles className="w-4 h-4 mr-1.5 text-secondary" />
              How to Train the Coach
            </h3>
            <p className="mt-2 text-xs text-secondary leading-relaxed">
              Interact with the AI Coach and give it instructions in the chat.
            </p>
          </div>

          <div className="space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">
              Example Commands
            </h4>
            <div className="space-y-2 text-xs text-gray-600">
              <p
                className="p-2.5 bg-gray-50 border border-gray-100 rounded-xl hover:bg-gray-100/50 transition-colors cursor-pointer"
                onClick={() =>
                  setInput(
                    "Always mention our 3-year warranty before discussing pricing packages.",
                  )
                }
              >
                "Always mention our 3-year warranty before discussing pricing
                packages."
              </p>
              <p
                className="p-2.5 bg-gray-50 border border-gray-100 rounded-xl hover:bg-gray-100/50 transition-colors cursor-pointer"
                onClick={() =>
                  setInput(
                    "Never guarantee exact delivery timelines. Say delivery is subject to availability.",
                  )
                }
              >
                "Never guarantee exact delivery timelines. Say delivery is
                subject to availability."
              </p>
              <p
                className="p-2.5 bg-gray-50 border border-gray-100 rounded-xl hover:bg-gray-100/50 transition-colors cursor-pointer"
                onClick={() =>
                  setInput(
                    "If pitching to a family buyer, emphasize fuel efficiency and active safety features.",
                  )
                }
              >
                "If pitching to a family buyer, emphasize fuel efficiency and
                active safety features."
              </p>
            </div>
          </div>
        </div>

        {/* Right chat panel */}
        <div className="flex-1 flex flex-col justify-between overflow-hidden relative">
          {/* Messages list container */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
            {messages.map((m) => (
              <div
                key={m.id || Math.random().toString()}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm shadow-sm ${
                    m.role === "user"
                      ? "bg-secondary text-white rounded-tr-none"
                      : "bg-white text-gray-800 border border-gray-200 rounded-tl-none"
                  }`}
                >
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {m.content}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messageEndRef} />
          </div>

          {/* Input control block */}
          <div className="p-4 bg-white border-t border-gray-200 flex-shrink-0 shadow-lg">
            <div className="flex gap-2 max-w-4xl mx-auto items-center">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Type your training rule or guideline here..."
                disabled={sending}
                className="flex-1 block rounded-xl border border-gray-300 px-4 py-3 text-gray-900 focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary text-sm"
              />
              <button
                onClick={() => handleSend()}
                disabled={sending || !input.trim()}
                className="p-3 bg-secondary hover:bg-secondary-light text-white rounded-xl disabled:opacity-50 transition-all flex items-center justify-center flex-shrink-0 shadow-md shadow-[#207C9720]"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITrainer;
